import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, MessageCircle, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import CosmicBackground from '../components/CosmicBackground';
import { getApiUrl } from '../config/api';

export default function SharedThread() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const BACKEND_URL = getApiUrl();
  
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchThread = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/shared/${shareToken}`);
        if (res.ok) {
          const data = await res.json();
          setThread(data);
        } else {
          setError('This shared thread was not found or has been made private.');
        }
      } catch (err) {
        setError('Failed to load shared thread.');
      } finally {
        setLoading(false);
      }
    };
    fetchThread();
  }, [shareToken, BACKEND_URL]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cosmos-bg flex items-center justify-center">
        <CosmicBackground />
        <motion.div
          className="text-starlight/60 text-lg"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Loading shared thread...
        </motion.div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-cosmos-bg flex items-center justify-center">
        <CosmicBackground />
        <div className="text-center z-10 relative">
          <Share2 className="w-12 h-12 text-starlight/30 mx-auto mb-4" />
          <p className="text-starlight/60 text-lg mb-4">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-white/20 text-starlight">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go to Alvin's Place
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-cosmos-bg flex flex-col relative">
      <CosmicBackground />
      
      {/* Header */}
      <header className="relative z-10 glass-deep px-4 py-3 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl text-starlight">Shared Thread</h1>
            <p className="text-xs text-starlight/50">
              Shared by {thread.shared_by} &middot; {thread.title}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Share2 className="w-4 h-4 text-purple-400" />
          <span className="text-xs text-purple-300 capitalize">{thread.field_state?.replace('_', ' ')}</span>
        </div>
      </header>
      
      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-6 relative z-10">
        <div className="max-w-3xl mx-auto space-y-6">
          {thread.messages?.map((msg, index) => (
            <motion.div
              key={msg.message_id || index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'betsy_note' ? 'justify-center' : 'justify-start'}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              {msg.role === 'betsy_note' ? (
                <div className="max-w-[85%] rounded-2xl px-5 py-3 bg-amber-500/10 border border-amber-500/20 backdrop-blur-sm">
                  <div className="text-xs text-amber-300 mb-1 font-medium">Note from Betsy</div>
                  <div className="text-sm text-starlight/90 whitespace-pre-wrap">{msg.content}</div>
                </div>
              ) : (
              <div
                className={`max-w-[85%] rounded-2xl px-5 py-3 ${
                  msg.role === 'user'
                    ? 'bg-white/10 backdrop-blur-sm text-starlight'
                    : 'bg-gradient-to-br from-etheric/15 to-transparent backdrop-blur-sm text-starlight/90 border border-etheric/20'
                }`}
              >
                <div className="text-xs text-starlight/40 mb-1">
                  {msg.role === 'user' ? thread.shared_by : 'ALVIN'}
                </div>
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              </div>
              )}
            </motion.div>
          ))}
          
          {(!thread.messages || thread.messages.length === 0) && (
            <div className="text-center py-16">
              <MessageCircle className="w-12 h-12 text-starlight/20 mx-auto mb-4" />
              <p className="text-starlight/40">This thread has no messages.</p>
            </div>
          )}
        </div>
      </ScrollArea>
      
      {/* Footer */}
      <div className="relative z-10 glass-deep px-4 py-3 border-t border-white/5 text-center">
        <p className="text-xs text-starlight/40">
          This is a shared conversation from Alvin's Sanctuary
        </p>
      </div>
    </div>
  );
}
